import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faFilter,
  faChartBar,
  faSync,
  faTable,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine
} from 'recharts';
import * as XLSX from 'xlsx';
import './UploadDashboard.css';

/**
 * A dashboard component that displays charts and metrics for uploads.
 * 
 * @param {Array} uploads - Array of upload data objects.
 */
function UploadDashboard({ uploads }) {
  // State for filtering and selection
  const [selectedAnalysisTypes, setSelectedAnalysisTypes] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredUploads, setFilteredUploads] = useState([]);
  
  // Extract unique values for filters
  const analysisTypes = useMemo(() => {
    const types = {};
    uploads.forEach(u => {
      if (u.analysisCode) {
        types[u.analysisCode] = true;
      }
    });
    return Object.keys(types).sort();
  }, [uploads]);
  
  const projectIds = useMemo(() => {
    const projects = {};
    uploads.forEach(u => {
      if (u.projectId) {
        projects[u.projectId] = true;
      }
    });
    return Object.keys(projects).sort();
  }, [uploads]);
  
  const sampleCodes = useMemo(() => {
    const samples = {};
    uploads.forEach(u => {
      if (u.sampleCode) {
        samples[u.sampleCode] = true;
      }
    });
    return Object.keys(samples).sort();
  }, [uploads]);
  
  const statusTypes = useMemo(() => {
    const statuses = {};
    uploads.forEach(u => {
      if (u.status) {
        statuses[u.status] = true;
      }
    });
    return Object.keys(statuses).sort();
  }, [uploads]);
  
  // Date range
  const dateExtent = useMemo(() => {
    if (uploads.length === 0) return { min: new Date(), max: new Date() };
    
    let min = new Date(uploads[0].upload_time);
    let max = new Date(uploads[0].upload_time);
    
    uploads.forEach(u => {
      const date = new Date(u.upload_time);
      if (date < min) min = date;
      if (date > max) max = date;
    });
    
    // Ensure we have a range of at least one month
    if (min.getTime() === max.getTime()) {
      min = new Date(min);
      min.setMonth(min.getMonth() - 1);
    }
    
    return { min, max };
  }, [uploads]);
  // Apply filters to uploads
  useEffect(() => {
    let filtered = [...uploads];
    
    // Filter by analysis type
    if (selectedAnalysisTypes.length > 0) {
      filtered = filtered.filter(u => selectedAnalysisTypes.includes(u.analysisCode));
    }
    
    // Filter by project
    if (selectedProjects.length > 0) {
      filtered = filtered.filter(u => selectedProjects.includes(u.projectId));
    }
    
    // Filter by sample type
    if (selectedSamples.length > 0) {
      filtered = filtered.filter(u => selectedSamples.includes(u.sampleCode));
    }
    
    // Filter by status
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(u => selectedStatuses.includes(u.status));
    }
    
    // Filter by date range
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(u => {
        const date = new Date(u.upload_time);
        return date >= dateRange.start && date <= dateRange.end;
      });
    }
    
    setFilteredUploads(filtered);
  }, [uploads, selectedAnalysisTypes, selectedProjects, selectedSamples, selectedStatuses, dateRange]);
  
  // Generate calendar days for full month view
  const calendarData = useMemo(() => {
    // Start with the beginning of the month of the min date
    const startDate = new Date(dateExtent.min);
    startDate.setDate(1);
    
    // End with the end of the month of the max date
    const endDate = new Date(dateExtent.max);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0); // Last day of the month
    
    // Create array of all days
    const allDays = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateString = currentDate.toISOString().split('T')[0];
      allDays.push({
        date: dateString,
        dateObj: new Date(currentDate),
        uploads: [],
        count: 0,
        sizeGB: 0,
        byAnalysis: {},
        byStatus: {}
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Populate with actual data
    const uploadsByDate = {};
    
    // Use filtered uploads if available
    (filteredUploads.length > 0 ? filteredUploads : uploads).forEach(upload => {
      const dateString = new Date(upload.upload_time).toISOString().split('T')[0];
      
      if (!uploadsByDate[dateString]) {
        uploadsByDate[dateString] = {
          uploads: [],
          count: 0,
          sizeGB: 0,
          byAnalysis: {},
          byStatus: {}
        };
      }
      
      // Add upload to list
      uploadsByDate[dateString].uploads.push(upload);
      uploadsByDate[dateString].count += 1;
      
      // Track by analysis type
      if (upload.analysisCode) {
        if (!uploadsByDate[dateString].byAnalysis[upload.analysisCode]) {
          uploadsByDate[dateString].byAnalysis[upload.analysisCode] = {
            count: 0,
            sizeGB: 0
          };
        }
        uploadsByDate[dateString].byAnalysis[upload.analysisCode].count += 1;
      }
      
      // Track by status
      if (upload.status) {
        if (!uploadsByDate[dateString].byStatus[upload.status]) {
          uploadsByDate[dateString].byStatus[upload.status] = 0;
        }
        uploadsByDate[dateString].byStatus[upload.status] += 1;
      }
      
      // Calculate total size
      const sizeGB = Array.isArray(upload.fileSize)
        ? upload.fileSize.reduce((s, fs) => s + (fs.gb || 0), 0)
        : (upload.fileSize?.gb || 0);
      
      uploadsByDate[dateString].sizeGB += sizeGB;
      
      // Add size to analysis type tracking
      if (upload.analysisCode && uploadsByDate[dateString].byAnalysis[upload.analysisCode]) {
        uploadsByDate[dateString].byAnalysis[upload.analysisCode].sizeGB += sizeGB;
      }
    });
    
    // Merge data into all days
    return allDays.map(day => {
      const dateData = uploadsByDate[day.date] || {
        uploads: [],
        count: 0,
        sizeGB: 0,
        byAnalysis: {},
        byStatus: {}
      };
      
      return {
        ...day,
        ...dateData
      };
    });
  }, [filteredUploads, uploads, dateExtent.min, dateExtent.max]);
  
  // Group calendar data by month and week
  const calendarByMonth = useMemo(() => {
    const months = {};
    
    calendarData.forEach(day => {
      const monthKey = `${day.dateObj.getFullYear()}-${(day.dateObj.getMonth() + 1).toString().padStart(2, '0')}`;
      
      if (!months[monthKey]) {
        months[monthKey] = {
          month: monthKey,
          name: day.dateObj.toLocaleDateString('default', { month: 'long', year: 'numeric' }),
          days: [],
          weeks: []
        };
      }
      
      months[monthKey].days.push(day);
    });
    
    // Organize days into weeks
    Object.values(months).forEach(month => {
      // Sort days by date
      month.days.sort((a, b) => a.dateObj - b.dateObj);
      
      // Get first day of month
      const firstDay = month.days[0];
      const firstDayOfWeek = firstDay.dateObj.getDay();
      
      // Create empty days for padding if month doesn't start on Sunday
      const paddingBefore = [];
      for (let i = 0; i < firstDayOfWeek; i++) {
        paddingBefore.push(null);
      }
      
      // Combine padding with actual days
      const allDays = [...paddingBefore, ...month.days];
      
      // Split into weeks
      const weeks = [];
      for (let i = 0; i < allDays.length; i += 7) {
        weeks.push(allDays.slice(i, i + 7));
      }
      
      // Pad last week if needed
      const lastWeek = weeks[weeks.length - 1];
      if (lastWeek.length < 7) {
        for (let i = lastWeek.length; i < 7; i++) {
          lastWeek.push(null);
        }
      }
      
      month.weeks = weeks;
    });
    
    return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
  }, [calendarData]);
  
  // Analysis data for charts
  const analysisTrends = useMemo(() => {
    // Group by date and analysis type
    const dateAnalysisMap = {};
    
    (filteredUploads.length > 0 ? filteredUploads : uploads).forEach(upload => {
      const dateStr = new Date(upload.upload_time).toISOString().split('T')[0];
      const analysis = upload.analysisCode || 'Unknown';
      
      if (!dateAnalysisMap[dateStr]) {
        dateAnalysisMap[dateStr] = { date: dateStr };
      }
      
      if (!dateAnalysisMap[dateStr][analysis]) {
        dateAnalysisMap[dateStr][analysis] = {
          count: 0,
          sizeGB: 0
        };
      }
      
      // Increment count
      dateAnalysisMap[dateStr][analysis].count += 1;
      
      // Add size
      const sizeGB = Array.isArray(upload.fileSize)
        ? upload.fileSize.reduce((s, fs) => s + (fs.gb || 0), 0)
        : (upload.fileSize?.gb || 0);
      
      dateAnalysisMap[dateStr][analysis].sizeGB += sizeGB;
    });
    
    // Convert to array and sort by date
    return Object.values(dateAnalysisMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(item => {
        // Flatten the analysis objects
        const result = { date: item.date };
        
        analysisTypes.forEach(type => {
          if (item[type]) {
            result[`${type}_count`] = item[type].count;
            result[`${type}_size`] = item[type].sizeGB.toFixed(2);
          } else {
            result[`${type}_count`] = 0;
            result[`${type}_size`] = 0;
          }
        });
        
        return result;
      });
  }, [filteredUploads, uploads, analysisTypes]);
  
  // Summary metrics
  const metrics = useMemo(() => {
    const data = filteredUploads.length > 0 ? filteredUploads : uploads;
    
    // Total uploads
    const totalUploads = data.length;
    
    // Total size
    const totalSizeGB = data.reduce((total, u) => {
      const size = Array.isArray(u.fileSize)
        ? u.fileSize.reduce((s, fs) => s + (fs.gb || 0), 0)
        : (u.fileSize?.gb || 0);
      return total + size;
    }, 0);
    
    // Status counts
    const statusCounts = {};
    statusTypes.forEach(status => {
      statusCounts[status] = data.filter(u => u.status === status).length;
    });
    
    // Analysis type counts
    const analysisCounts = {};
    analysisTypes.forEach(type => {
      analysisCounts[type] = data.filter(u => u.analysisCode === type).length;
    });
    
    // Average size per upload
    const avgSizeGB = totalUploads > 0 ? totalSizeGB / totalUploads : 0;
    
    // Processing times
    const processingTimes = data
      .filter(u => u.initiated_time && u.completed_time)
      .map(u => {
        const start = new Date(u.initiated_time);
        const end = new Date(u.completed_time);
        return (end - start) / 1000 / 60; // minutes
      });
    
    const avgProcessingTime = processingTimes.length > 0
      ? processingTimes.reduce((sum, time) => sum + time, 0) / processingTimes.length
      : 0;
    
    return {
      totalUploads,
      totalSizeGB: totalSizeGB.toFixed(2),
      statusCounts,
      analysisCounts,
      avgSizeGB: avgSizeGB.toFixed(2),
      avgProcessingTime: avgProcessingTime.toFixed(2),
      processingTimes
    };
  }, [filteredUploads, uploads, statusTypes, analysisTypes]);
  
  // Helper function to toggle selection in an array
  const toggleSelection = (array, item) => {
    return array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
  };
  
  // Function to handle date selection in calendar
  const handleDateClick = (day) => {
    if (!day) return; // Ignore null days (padding)
    
    setSelectedDate(selectedDate && selectedDate === day.date ? null : day.date);
    
    if (day.date && day.date !== selectedDate) {
      // Update date range to this specific day
      const clickedDate = new Date(day.date);
      const nextDay = new Date(clickedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      setDateRange({
        start: clickedDate,
        end: nextDay
      });
    }
  };
  
  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="UTM-tooltip">
          <div className="UTM-tooltip-label">{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="UTM-tooltip-item">
              <span className="UTM-tooltip-key">{entry.name || entry.dataKey}:</span>
              <span className="UTM-tooltip-value" style={{ color: entry.color }}>
                {entry.value} {entry.name === 'Size' ? 'GB' : ''}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  
  // Export data to Excel
  const exportToExcel = () => {
    const data = filteredUploads.length > 0 ? filteredUploads : uploads;
    
    // Transform data for export
    const rows = data.map(u => ({
      Date: new Date(u.upload_time).toLocaleDateString(),
      Time: new Date(u.upload_time).toLocaleTimeString(),
      ID: u.id,
      Patient: u.patientId,
      Analysis: u.analysisCode,
      Sample: u.sampleCode,
      Project: u.projectId,
      Visit: u.visitNumber,
      Status: u.status,
      'Original Files': Array.isArray(u.originalFileNames) ? u.originalFileNames.join(', ') : u.originalFileNames,
      'New Files': Array.isArray(u.newFileName) ? u.newFileName.join(', ') : u.newFileName,
      'Size (GB)': Array.isArray(u.fileSize)
        ? u.fileSize.reduce((s, fs) => s + (fs.gb || 0), 0).toFixed(2)
        : (u.fileSize?.gb || 0).toFixed(2),
      'Upload Time': u.upload_time,
      'Process Start': u.initiated_time,
      'Process End': u.completed_time,
      'Processing Time (min)': u.initiated_time && u.completed_time
        ? ((new Date(u.completed_time) - new Date(u.initiated_time)) / 1000 / 60).toFixed(2)
        : '',
      Result: u.resultCode
    }));
    
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Uploads');
    XLSX.writeFile(wb, 'upload_data.xlsx');
  };
  
  // Reset all filters
  const resetFilters = () => {
    setSelectedAnalysisTypes([]);
    setSelectedProjects([]);
    setSelectedSamples([]);
    setSelectedStatuses([]);
    setDateRange({ start: null, end: null });
    setSelectedDate(null);
  };
  
  return (
    <div className="UTM-dashboard">
      {/* Filter panel */}
      <div className="UTM-filter-panel">
        <div className="UTM-filter-header">
          <FontAwesomeIcon icon={faFilter} />
          <h3>Filters</h3>
          <div className="UTM-filter-actions">
            <button className="UTM-action-button" onClick={resetFilters}>
              <FontAwesomeIcon icon={faSync} />
              <span>Reset</span>
            </button>
            <button className="UTM-action-button" onClick={exportToExcel}>
              <FontAwesomeIcon icon={faFileExport} />
              <span>Export</span>
            </button>
          </div>
        </div>
        
        <div className="UTM-filter-sections">
          <div className="UTM-filter-section">
            <div className="UTM-filter-section-header">Analysis Type</div>
            <div className="UTM-filter-chips">
              {analysisTypes.map(type => (
                <div 
                  key={type}
                  className={`UTM-filter-chip ${selectedAnalysisTypes.includes(type) ? 'UTM-filter-chip-selected' : ''}`}
                  onClick={() => setSelectedAnalysisTypes(toggleSelection(selectedAnalysisTypes, type))}
                >
                  {type}
                  <span className="UTM-chip-count">{metrics.analysisCounts[type] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="UTM-filter-section">
            <div className="UTM-filter-section-header">Project</div>
            <div className="UTM-filter-chips">
              {projectIds.map(id => (
                <div 
                  key={id}
                  className={`UTM-filter-chip ${selectedProjects.includes(id) ? 'UTM-filter-chip-selected' : ''}`}
                  onClick={() => setSelectedProjects(toggleSelection(selectedProjects, id))}
                >
                  {id}
                </div>
              ))}
            </div>
          </div>
          
          <div className="UTM-filter-section">
            <div className="UTM-filter-section-header">Sample Type</div>
            <div className="UTM-filter-chips">
              {sampleCodes.map(code => (
                <div 
                  key={code}
                  className={`UTM-filter-chip ${selectedSamples.includes(code) ? 'UTM-filter-chip-selected' : ''}`}
                  onClick={() => setSelectedSamples(toggleSelection(selectedSamples, code))}
                >
                  {code}
                </div>
              ))}
            </div>
          </div>
          
          <div className="UTM-filter-section">
            <div className="UTM-filter-section-header">Status</div>
            <div className="UTM-filter-chips">
              {statusTypes.map(status => (
                <div 
                  key={status}
                  className={`UTM-filter-chip ${selectedStatuses.includes(status) ? 'UTM-filter-chip-selected' : ''}`}
                  onClick={() => setSelectedStatuses(toggleSelection(selectedStatuses, status))}
                >
                  {status}
                  <span className="UTM-chip-count">{metrics.statusCounts[status] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary metrics */}
      <div className="UTM-metrics-row">
        <div className="UTM-metric-tile">
          <div className="UTM-metric-value">{metrics.totalUploads}</div>
          <div className="UTM-metric-label">Total Uploads</div>
        </div>
        
        <div className="UTM-metric-tile">
          <div className="UTM-metric-value">{metrics.totalSizeGB} GB</div>
          <div className="UTM-metric-label">Total Size</div>
        </div>
        
        <div className="UTM-metric-tile">
          <div className="UTM-metric-value">{metrics.avgSizeGB} GB</div>
          <div className="UTM-metric-label">Avg Size</div>
        </div>
        
        <div className="UTM-metric-tile">
          <div className="UTM-metric-value">{metrics.avgProcessingTime} min</div>
          <div className="UTM-metric-label">Avg Processing Time</div>
        </div>
      </div>
      
      {/* Main content grid */}
      <div className="UTM-content-grid">
        {/* Calendar panel */}
        <div className="UTM-calendar-container">
          <div className="UTM-panel-header">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <h3>Daily Activity</h3>
            {selectedDate && (
              <div className="UTM-date-selection">
                Selected: {new Date(selectedDate).toLocaleDateString()}
                <button className="UTM-clear-button" onClick={() => {
                  setSelectedDate(null);
                  setDateRange({ start: null, end: null });
                }}>×</button>
              </div>
            )}
          </div>
          
          {/* Calendar view */}
          <div className="UTM-calendar">
            {calendarByMonth.map((month, monthIndex) => (
              <div key={monthIndex} className="UTM-month-container">
                <div className="UTM-month-header">{month.name}</div>
                <div className="UTM-weekdays">
                  <div>Sun</div>
                  <div>Mon</div>
                  <div>Tue</div>
                  <div>Wed</div>
                  <div>Thu</div>
                  <div>Fri</div>
                  <div>Sat</div>
                </div>
                <div className="UTM-month-grid">
                  {month.weeks.map((week, weekIndex) => (
                    <React.Fragment key={weekIndex}>
                      {week.map((day, dayIndex) => (
                        <div 
                          key={dayIndex}
                          className={`UTM-day ${!day ? 'UTM-day-empty' : ''} ${
                            day && day.count > 0 ? 'UTM-day-with-data' : ''
                          } ${day && selectedDate === day.date ? 'UTM-day-selected' : ''}`}
                          onClick={() => day && handleDateClick(day)}
                        >
                          {day && (
                            <>
                              <div className="UTM-day-header">
                                <span className="UTM-day-number">{day.dateObj.getDate()}</span>
                                {day.count > 0 && (
                                  <span className="UTM-day-count">{day.count}</span>
                                )}
                              </div>
                              {day.count > 0 && (
                                <div className="UTM-day-analysis-bars">
                                  {Object.entries(day.byAnalysis).map(([type, data], i) => (
                                    <div 
                                      key={i} 
                                      className="UTM-analysis-bar"
                                      style={{
                                        width: `${Math.min(100, (data.count / day.count) * 100)}%`,
                                        backgroundColor: `rgba(24, 144, 255, ${0.4 + (i * 0.2)})`
                                      }}
                                      title={`${type}: ${data.count} uploads (${data.sizeGB.toFixed(2)} GB)`}
                                    >
                                      {type}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Charts panel */}
        <div className="UTM-charts-container">
          {/* Charts tabs header */}
          <div className="UTM-charts-header">
            <div className="UTM-panel-header">
              <FontAwesomeIcon icon={faChartBar} />
              <h3>Uploads by Analysis Type</h3>
            </div>
          </div>
          
          {/* Analysis trend chart */}
          <div className="UTM-chart-container">
            {analysisTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={analysisTrends}>
                  <defs>
                    {analysisTypes.map((type, index) => (
                      <linearGradient key={index} id={`gradient-${type}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={`rgba(24, 144, 255, ${0.6 + (index * 0.1)})`} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={`rgba(24, 144, 255, ${0.6 + (index * 0.1)})`} stopOpacity={0.2} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={val => new Date(val).toLocaleDateString()} 
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Count', angle: -90, position: 'insideLeft', fontSize: 10 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Size (GB)', angle: 90, position: 'insideRight', fontSize: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Render a line and area for each analysis type */}
                  {analysisTypes.map((type, index) => (
                    <React.Fragment key={index}>
                      <Area
                        yAxisId="left"
                        dataKey={`${type}_count`}
                        name={`${type} Count`}
                        fill={`url(#gradient-${type})`}
                        stroke={`rgba(24, 144, 255, ${0.7 + (index * 0.1)})`}
                        fillOpacity={0.6}
                        stackId="count"
                      />
                      <Line
                        yAxisId="right"
                        dataKey={`${type}_size`}
                        name={`${type} Size`}
                        stroke={`rgba(24, 144, 255, ${0.7 + (index * 0.1)})`}
                        strokeDasharray="5 5"
                        dot={{ stroke: `rgba(24, 144, 255, ${0.8 + (index * 0.1)})`, strokeWidth: 2, r: 3 }}
                      />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="UTM-no-data">No data available for the selected filters</div>
            )}
          </div>
          
          {/* Selected day details */}
          {selectedDate && (
            <div className="UTM-selected-day-details">
              <div className="UTM-panel-header">
                <h3>Uploads for {new Date(selectedDate).toLocaleDateString()}</h3>
              </div>
              
              <div className="UTM-table-container">
                <table className="UTM-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Time</th>
                      <th>Patient</th>
                      <th>Analysis</th>
                      <th>Sample</th>
                      <th>Size</th>
                      <th>Status</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendarData
                      .find(day => day.date === selectedDate)?.uploads
                      .sort((a, b) => new Date(a.upload_time) - new Date(b.upload_time))
                      .map((upload, index) => {
                        const size = Array.isArray(upload.fileSize)
                          ? upload.fileSize.reduce((s, fs) => s + (fs.gb || 0), 0)
                          : (upload.fileSize?.gb || 0);
                        
                        const duration = upload.initiated_time && upload.completed_time
                          ? ((new Date(upload.completed_time) - new Date(upload.initiated_time)) / 1000 / 60).toFixed(2)
                          : '-';
                        
                        return (
                          <tr key={index}>
                            <td>{upload.id}</td>
                            <td>{new Date(upload.upload_time).toLocaleTimeString()}</td>
                            <td>{upload.patientId}</td>
                            <td>{upload.analysisCode}</td>
                            <td>{upload.sampleCode}</td>
                            <td>{size.toFixed(2)} GB</td>
                            <td className={`UTM-status-${upload.status.toLowerCase()}`}>{upload.status}</td>
                            <td>{duration} min</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UploadDashboard;
